import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { UserCollectionsFeedQuery } from 'apollo/userCollection/userCollection.gql';
import { Collection } from 'types';
import PaginatedVirtualScroll from '../PaginatedVirtualScroll';
import { CollectionsPerPage } from '../../constants';
import { CollectionItemHeight, CollectionsPage } from './CollectionsPage';

export interface CollectionsListProps {
    sampleIds?: string[];
    hide: () => void;
    searchString?: string;
    placeholder?: string;
}

export const StaticItemsCount = 1;

export const CollectionsList: React.FC<CollectionsListProps> = ({
    sampleIds,
    hide,
    searchString = '',
    placeholder,
}) => {
    const searchQuery = searchString ? `&textSearch=${searchString}` : '';

    const { data, loading, fetchMore } = useQuery(UserCollectionsFeedQuery, {
        variables: {
            offset: 0,
            query: searchQuery,
            take: CollectionsPerPage - StaticItemsCount,
        },
    });

    const items: Collection[] | undefined = data?.userCollectionsFeed.feed;

    const itemCount = items?.length;

    const totalItems = data?.userCollectionsFeed.meta?.totalRecords || 0;

    const pageCount = Math.ceil(totalItems / CollectionsPerPage);

    const pageComponent = useCallback(
        ({ page }: { page: number }) => {
            return (
                <CollectionsPage
                    fetchMore={fetchMore as any}
                    hide={hide}
                    items={items}
                    loading={loading}
                    pageCount={pageCount}
                    pageIndex={page}
                    placeholder={placeholder}
                    sampleIds={sampleIds}
                    searchQuery={searchQuery}
                    totalItems={totalItems}
                />
            );
        },
        // This will cause multiple fetchMore calls if we don't restrict the
        // dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchQuery, totalItems, loading, itemCount],
    );

    const getPageHeight = useCallback(
        (index: number) => {
            if (!totalItems) {
                return CollectionItemHeight;
            }

            const pageNumber = index + 1;
            const totalItemsWithStaticItems = totalItems + StaticItemsCount;

            if (pageNumber === pageCount) {
                return (
                    (totalItemsWithStaticItems - index * CollectionsPerPage) *
                    CollectionItemHeight
                );
            }

            return CollectionsPerPage * CollectionItemHeight;
        },
        [pageCount, totalItems],
    );

    const scroll = useMemo(
        () => (
            <PaginatedVirtualScroll
                height={315}
                estimatedItemSize={CollectionItemHeight}
                itemsPerPage={pageCount === 1 ? totalItems : CollectionsPerPage}
                totalItems={pageCount}
                pageComponent={pageComponent}
                itemSize={getPageHeight}
                overscanCount={1}
            />
        ),
        // This will cause multiple fetchMore calls if we don't restrict the
        // dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [totalItems, itemCount],
    );

    return scroll;
};
